import {
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  useGetShippingSettings,
  useUpdateShippingSettings,
} from '../hooks/backend/settingsPage'

// ─── Types ─────────────────────────────────────────────────────────────────

export type ShippingProvider = 'shippo' | 'envia'

export type EnviaCarrier = 'ups' | 'fedex' | 'usps'

export const ENVIA_CARRIER_OPTIONS: Array<{
  id: EnviaCarrier
  name: string
}> = [
  { id: 'ups', name: 'UPS' },
  { id: 'fedex', name: 'FedEx' },
  { id: 'usps', name: 'USPS' },
]

export interface ShipFromAddress {
  name: string
  company: string
  street1: string
  street2: string
  city: string
  state: string
  zip: string
  phone: string
  email: string
}

export interface ParcelPreset {
  id: string
  name: string
  length: number
  width: number
  height: number
}

export interface ShippingSettings {
  shippingProvider: ShippingProvider
  enviaCarriers: EnviaCarrier[]
  shipFromAddress: ShipFromAddress
  parcelPresets: ParcelPreset[]
  defaultParcelPresetId: string
}

// ─── Defaults ──────────────────────────────────────────────────────────────

export const DEFAULT_SHIP_FROM_ADDRESS: ShipFromAddress = {
  name: "Poppin' West Texas, LLC",
  company: "Poppin' West Texas, LLC",
  street1: '3710 Woodrow Rd. #189',
  street2: '',
  city: 'Lubbock',
  state: 'TX',
  zip: '79423',
  phone: '8066301005',
  email: 'accounts@poppinwtx.com',
}

export const DEFAULT_PARCEL_PRESETS: ParcelPreset[] = [
  {
    id: 'default',
    name: 'Standard Box (23 x 23 x 13 in)',
    length: 23,
    width: 23,
    height: 13,
  },
]

const DEFAULT_SETTINGS: ShippingSettings = {
  shippingProvider: 'shippo',
  enviaCarriers: ['ups'],
  shipFromAddress: DEFAULT_SHIP_FROM_ADDRESS,
  parcelPresets: DEFAULT_PARCEL_PRESETS,
  defaultParcelPresetId: 'default',
}

// ─── Shared event ──────────────────────────────────────────────────────────
//
// This keeps multiple components on the SAME computer in sync immediately
// after settings are saved. The database handles persistence across computers.

const SETTINGS_EVENT = 'shipping-settings-change'

function notifySettingsChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SETTINGS_EVENT))
  }
}

// ─── Normalize backend data ────────────────────────────────────────────────

function normalizeSettings(value: unknown): ShippingSettings {
  if (!value || typeof value !== 'object') {
    return DEFAULT_SETTINGS
  }

  const data = value as Partial<ShippingSettings>

  const shippingProvider: ShippingProvider =
    data.shippingProvider === 'envia'
      ? 'envia'
      : 'shippo'

  const enviaCarriers: EnviaCarrier[] =
    Array.isArray(data.enviaCarriers)
      ? data.enviaCarriers.filter(
          (carrier): carrier is EnviaCarrier =>
            carrier === 'ups' ||
            carrier === 'fedex' ||
            carrier === 'usps'
        )
      : ['ups']

  const parcelPresets =
    Array.isArray(data.parcelPresets) &&
    data.parcelPresets.length > 0
      ? data.parcelPresets
      : DEFAULT_PARCEL_PRESETS

  const requestedDefault =
    data.defaultParcelPresetId

  const defaultParcelPresetId =
    requestedDefault &&
    parcelPresets.some(
      preset => preset.id === requestedDefault
    )
      ? requestedDefault
      : parcelPresets[0]?.id ?? ''

  return {
    shippingProvider,

    enviaCarriers:
      enviaCarriers.length > 0
        ? enviaCarriers
        : ['ups'],

    shipFromAddress: {
      ...DEFAULT_SHIP_FROM_ADDRESS,
      ...(data.shipFromAddress ?? {}),
    },

    parcelPresets,

    defaultParcelPresetId,
  }
}

// ─── Main database-backed hook ─────────────────────────────────────────────

export function useShippingSettings() {
  const getSettings = useGetShippingSettings()
  const updateSettings = useUpdateShippingSettings()

  const [settings, setSettings] =
    useState<ShippingSettings>(DEFAULT_SETTINGS)

  const [loaded, setLoaded] =
    useState(false)

  const load = useCallback(async () => {
    try {
      const result =
        await getSettings.trigger()

      const normalized =
        normalizeSettings(result)

      setSettings(normalized)
      setLoaded(true)

      return normalized
    } catch (error) {
      console.error(
        'Failed to load shipping settings:',
        error
      )

      setLoaded(true)

      throw error
    }
  }, [getSettings.trigger])

  // Load from Retool DB when component mounts.

  useEffect(() => {
    void load()
  }, [load])

  // If another component saves settings,
  // reload them here.

  useEffect(() => {
    const sync = () => {
      void load()
    }

    window.addEventListener(
      SETTINGS_EVENT,
      sync
    )

    return () => {
      window.removeEventListener(
        SETTINGS_EVENT,
        sync
      )
    }
  }, [load])

  const save = useCallback(
    async (
      next: ShippingSettings
    ) => {
      await updateSettings.trigger({
        shipping_provider:
          next.shippingProvider,

        envia_carriers:
          next.enviaCarriers,

        ship_from_address:
          next.shipFromAddress,

        parcel_presets:
          next.parcelPresets,

        default_parcel_preset_id:
          next.defaultParcelPresetId,
      })

      setSettings(next)

      notifySettingsChanged()

      return next
    },
    [updateSettings.trigger]
  )

  return {
    settings,
    loaded,

    loading:
      getSettings.loading ||
      updateSettings.loading,

    error:
      getSettings.error ||
      updateSettings.error,

    reload: load,
    save,
  }
}

// ─── Shipping provider ─────────────────────────────────────────────────────

export function useShippingProvider(): [
  ShippingProvider,
  (provider: ShippingProvider) => Promise<void>,
] {
  const {
    settings,
    save,
  } = useShippingSettings()

  const update = useCallback(
    async (
      provider: ShippingProvider
    ) => {
      await save({
        ...settings,
        shippingProvider: provider,
      })
    },
    [settings, save]
  )

  return [
    settings.shippingProvider,
    update,
  ]
}

// ─── Envia carriers ────────────────────────────────────────────────────────

export function useEnviaCarriers(): [
  EnviaCarrier[],
  (carriers: EnviaCarrier[]) => Promise<void>,
] {
  const {
    settings,
    save,
  } = useShippingSettings()

  const update = useCallback(
    async (
      carriers: EnviaCarrier[]
    ) => {
      await save({
        ...settings,

        enviaCarriers:
          carriers.length > 0
            ? carriers
            : ['ups'],
      })
    },
    [settings, save]
  )

  return [
    settings.enviaCarriers,
    update,
  ]
}

// ─── Ship-from address ─────────────────────────────────────────────────────

export function useShipFromAddress(): [
  ShipFromAddress,
  (address: ShipFromAddress) => Promise<void>,
] {
  const {
    settings,
    save,
  } = useShippingSettings()

  const update = useCallback(
    async (
      address: ShipFromAddress
    ) => {
      await save({
        ...settings,
        shipFromAddress: address,
      })
    },
    [settings, save]
  )

  return [
    settings.shipFromAddress,
    update,
  ]
}

// ─── Parcel presets ────────────────────────────────────────────────────────

export function useParcelPresets(): {
  presets: ParcelPreset[]
  defaultPresetId: string
  updatePresets: (
    presets: ParcelPreset[]
  ) => Promise<void>
  updateDefaultPresetId: (
    id: string
  ) => Promise<void>
} {
  const {
    settings,
    save,
  } = useShippingSettings()

  const updatePresets =
    useCallback(
      async (
        presets: ParcelPreset[]
      ) => {
        if (!presets.length) {
          return
        }

        let defaultId =
          settings.defaultParcelPresetId

        if (
          !presets.some(
            preset =>
              preset.id === defaultId
          )
        ) {
          defaultId =
            presets[0]?.id ?? ''
        }

        await save({
          ...settings,

          parcelPresets:
            presets,

          defaultParcelPresetId:
            defaultId,
        })
      },
      [settings, save]
    )

  const updateDefaultPresetId =
    useCallback(
      async (
        id: string
      ) => {
        if (
          !settings.parcelPresets.some(
            preset =>
              preset.id === id
          )
        ) {
          return
        }

        await save({
          ...settings,

          defaultParcelPresetId:
            id,
        })
      },
      [settings, save]
    )

  return {
    presets:
      settings.parcelPresets,

    defaultPresetId:
      settings.defaultParcelPresetId,

    updatePresets,
    updateDefaultPresetId,
  }
}

// ─── Parcel ID helper ──────────────────────────────────────────────────────

export function createParcelPresetId(): string {
  return `parcel-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`
}